import { useMemo, useState } from "react";
import api from "@/lib/api";
import { artUrl } from "@/lib/uploadArt";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Scissors, Loader2, FileDown, CheckCircle2, Layers } from "lucide-react";

const MODES = [
  { key: "shape", label: "Cut to shape" },
  { key: "kiss_cut", label: "Kiss-Cut (figure)" },
  { key: "cut_only", label: "Cut only (letters)" },
  { key: "bleed_only", label: "Bleed only" },
];
const SHAPES = [
  { key: "rectangle", label: "Rectangle" },
  { key: "rounded", label: "Rounded" },
  { key: "circle", label: "Circle" },
  { key: "rhombus", label: "Rhombus" },
];
const OUTPUTS = [
  { key: "pdf", label: "PDF" },
  { key: "png", label: "PNG (DTF)" },
  { key: "svg", label: "SVG (Laser)" },
];

// Generate one production file per row that has artwork, zipped together.
export default function BatchCutDialog({ open, onOpenChange, rows = [], isSticker = false }) {
  const [mode, setMode] = useState(isSticker ? "kiss_cut" : "shape");
  const [shape, setShape] = useState("rectangle");
  const [outFmt, setOutFmt] = useState("pdf");
  const [bleed, setBleed] = useState("0.25");
  const [offset, setOffset] = useState("0.125");
  const [radius, setRadius] = useState("0.25");
  const [marks, setMarks] = useState("auto");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const eligible = useMemo(
    () => (rows || []).filter((r) => r.art_id && +r.width > 0 && +r.height > 0),
    [rows]
  );

  const generate = async () => {
    if (!eligible.length) { toast.error("No rows with uploaded artwork and a size"); return; }
    setLoading(true);
    try {
      const items = eligible.map((r) => ({
        art_id: r.art_id, final_w: +r.width, final_h: +r.height, unit: "in",
        mode, shape, bleed: +bleed || 0.25, offset: +offset || 0.125,
        corner_radius: +radius || 0.25, margin_marks: marks, output_format: outFmt,
        name: r.name || null,
      }));
      const { data } = await api.post("/production/prep-cut-batch", { items });
      setResult(data);
      toast.success(`${data.count} production file(s) ready`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build the batch");
    }
    setLoading(false);
  };

  const pill = (active) =>
    `rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${active ? "border-[#2495D3] bg-[#2495D3]/10 text-[#2495D3]" : "border-slate-200 text-slate-600 hover:border-slate-300"}`;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) setResult(null); onOpenChange(o); }}>
      <DialogContent className="max-w-2xl" data-testid="batchcut-dialog">
        <DialogHeader>
          <DialogTitle className="font-head">Batch production files &middot; Cut &amp; Bleed</DialogTitle>
          <DialogDescription>Generate a VersaWorks-ready file for every row that has uploaded artwork, zipped together.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-sm text-slate-600 flex items-center gap-2" data-testid="batchcut-count">
            <Layers size={15} className="text-[#2495D3]" />
            <span><b>{eligible.length}</b> row(s) with artwork will be processed{rows.length > eligible.length ? ` \u00b7 ${rows.length - eligible.length} skipped (no art/size)` : ""}</span>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-600">Cut mode (applied to all)</label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {MODES.map((m) => (
                <button key={m.key} type="button" data-testid={`batch-mode-${m.key}`} onClick={() => setMode(m.key)} className={pill(mode === m.key)}>{m.label}</button>
              ))}
            </div>
          </div>

          {mode === "shape" && (
            <div>
              <label className="text-xs font-semibold text-slate-600">Shape</label>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {SHAPES.map((s) => (
                  <button key={s.key} type="button" data-testid={`batch-shape-${s.key}`} onClick={() => setShape(s.key)} className={pill(shape === s.key)}>{s.label}</button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="text-xs font-semibold text-slate-600">Output format</label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {OUTPUTS.map((o) => (
                <button key={o.key} type="button" data-testid={`batch-out-${o.key}`} onClick={() => setOutFmt(o.key)} className={pill(outFmt === o.key)}>{o.label}</button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-600">Bleed (in)</label>
              <Input data-testid="batch-bleed" type="number" step="0.0625" value={bleed} onChange={(e) => setBleed(e.target.value)} className="mt-1 rounded-lg num" />
            </div>
            {(mode === "kiss_cut" || mode === "cut_only") && (
              <div>
                <label className="text-xs font-semibold text-slate-600">Cut offset (in)</label>
                <Input data-testid="batch-offset" type="number" step="0.0625" value={offset} onChange={(e) => setOffset(e.target.value)} className="mt-1 rounded-lg num" />
              </div>
            )}
            {mode === "shape" && shape === "rounded" && (
              <div>
                <label className="text-xs font-semibold text-slate-600">Corner radius (in)</label>
                <Input data-testid="batch-radius" type="number" step="0.0625" value={radius} onChange={(e) => setRadius(e.target.value)} className="mt-1 rounded-lg num" />
              </div>
            )}
            <div>
              <label className="text-xs font-semibold text-slate-600">Margin marks</label>
              <select data-testid="batch-marks" value={marks} onChange={(e) => setMarks(e.target.value)} className="mt-1 w-full h-9 rounded-lg border border-slate-300 text-sm px-2 bg-white">
                <option value="auto">Auto (white bg)</option>
                <option value="on">Always</option>
                <option value="off">Never</option>
              </select>
            </div>
          </div>

          <Button data-testid="batch-generate" onClick={generate} disabled={loading || !eligible.length} className="w-full rounded-lg bg-[#2495D3] hover:bg-[#1E7AA9]">
            {loading ? <><Loader2 size={16} className="mr-2 animate-spin" />Building {eligible.length} file(s)&hellip;</> : <><Scissors size={16} className="mr-2" />Generate {eligible.length} production file(s)</>}
          </Button>

          {result && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid="batch-result">
              <p className="text-sm font-semibold text-emerald-800 flex items-center gap-1.5"><CheckCircle2 size={16} />{result.count} file(s) zipped</p>
              <a href={artUrl(result.file_id)} target="_blank" rel="noreferrer" data-testid="batch-download">
                <Button className="mt-2 rounded-lg bg-emerald-600 hover:bg-emerald-700"><FileDown size={15} className="mr-1.5" />Download ZIP</Button>
              </a>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
